import { Field as ChakraField, Fieldset, VStack } from '@chakra-ui/react';
import { FormInputNumber } from '@components/common/form';
import ModifiedTag from '@components/common/form/ModifiedField';
import PopupContainer from '@components/map/popup/common/PopupContainer';
import { GPSCoordinatesField } from '@components/ui/coordinatesInput';
import { DraggablePanel } from '@components/ui/draggable';
import { FormMap } from '@components/ui/map/formMap/FormMap';
import { useFormMapContext, usePointFormMapContext } from '@components/ui/map/formMap/FormMapContext';
import { useMapCursor } from '@components/ui/map/formMap/useMapCursor';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { useMapSelectPosition } from '@hooks/useMapSelectPosition';
import { useLazyRetrieveCoordinatesAltitudeQuery } from '@services/c2/c2.api';
import { colors } from '@theme/foundations/colors';
import { isValidCoordinates, toLonLatArray } from '@utils/validation/coordinates.utils';
import { hasChanged, isInvalid } from '@utils/validation/validation.utils';
import { Field, FieldProps, useFormikContext } from 'formik';
import { Feature } from 'geojson';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CircleLayerSpecification, Layer, Source } from 'react-map-gl/mapbox';

import { EditableSensorOrSubSensorWithShorthand } from '@/types/sensor/sensorInstallation.types';
import { CommonConfiguration } from '@/types/sensor/sensors';

import SensorFormNavigationArrows from '../../SensorFormNavigationArrows';
import { SensorFormType } from './CommonFormInputs';

function SensorPositionFormPopup() {
  const { formatMessage } = useIntl();
  const { mapConstraintsRef, mouseSelectedPosition, isSelectionEnabled, setSelectionOn, setSelectedPosition } =
    useFormMapContext();
  useMapSelectPosition(isSelectionEnabled, setSelectedPosition);
  const { errors, touched, values, initialValues, setFieldValue } = useFormikContext<SensorFormType>();
  const [retrieve] = useLazyRetrieveCoordinatesAltitudeQuery();

  useEffect(() => {
    if (values.sensorPosition && isValidCoordinates(values.sensorPosition) && touched.sensorPosition) {
      retrieve({ coordinates: [values.sensorPosition] })
        .unwrap()
        .then((altitudes) => {
          const altitude: number | undefined = altitudes.length >= 1 ? altitudes[0] : undefined;
          console.log(altitude);
          altitude && setFieldValue('sensorAltitude', altitude);
        });
    }
  }, [retrieve, setFieldValue, touched.sensorPosition, values.sensorPosition]);

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={2} left={2}>
      <DraggablePanel.Header>
        <FormattedMessage id="components.sensorInstallation.form.localization" />
      </DraggablePanel.Header>
      <Fieldset.Root>
        <DraggablePanel.Body width={320} backgroundColor="neutral.900">
          <VStack gap={0.25} width="100%" height="100%">
            <PopupContainer>
              <ChakraField.Root variant="cockpit" required invalid={isInvalid('sensorPosition', touched, errors)}>
                <ChakraField.Label width="100%" height="20px">
                  <FormattedMessage id="components.sensorInstallation.form.common.sensorPosition" />
                  <RequiredIndicator />
                  {hasChanged('sensorPosition', initialValues, values) && <ModifiedTag />}
                </ChakraField.Label>
                <Fieldset.Content>
                  <Field name="sensorPosition">
                    {({ field }: FieldProps<EditableSensorOrSubSensorWithShorthand['sensorPosition']>) => {
                      const coords = {
                        latitude: field.value.latitude,
                        longitude: field.value.longitude,
                        type: '2D',
                      };
                      return (
                        <GPSCoordinatesField
                          mouseSelectedPosition={mouseSelectedPosition}
                          isSelectionEnabled={isSelectionEnabled}
                          setSelectionOn={setSelectionOn}
                          name="sensorPosition"
                          coordinates={coords}
                        />
                      );
                    }}
                  </Field>
                </Fieldset.Content>
              </ChakraField.Root>
            </PopupContainer>
            <PopupContainer>
              <FormInputNumber
                label="sensorAltitude"
                suffix="m"
                placeholder={formatMessage({ id: 'components.sensorInstallation.form.common.amsl' })}
              />
            </PopupContainer>
          </VStack>
        </DraggablePanel.Body>
      </Fieldset.Root>
    </DraggablePanel.Root>
  );
}

function SensorPositionLayer() {
  const { values } = useFormikContext<SensorFormType>();
  const { zoomOnPoint } = usePointFormMapContext();
  const position = toLonLatArray(values.sensorPosition);

  useMapCursor();
  useEffect(() => {
    if (values.sensorPosition) {
      zoomOnPoint(values.sensorPosition);
    }
  }, [values.sensorPosition, zoomOnPoint]);

  if (!position) return;

  const style: CircleLayerSpecification = {
    id: 'sensor-position',
    type: 'circle',
    source: 'SensorPosition',
    filter: ['all', ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 9,
      'circle-color': colors.blue[500].value,
    },
  };

  return (
    <Source
      id="SensorPosition"
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            id: 'SensorPosition',
            geometry: {
              type: 'Point',
              coordinates: position,
            },
          } as Feature,
        ],
      }}
    >
      <Layer {...style} />
    </Source>
  );
}

const commonSensorPositionFormValidationFields: (keyof CommonConfiguration)[] = ['sensorPosition', 'sensorAltitude'];

export function SensorPositionForm() {
  const { values } = useFormikContext<SensorFormType>();

  return (
    <FormMap
      mode={values.id ? 'edit' : 'create'}
      initialCenter={values.sensorPosition}
      drawMode="draw_point"
      hideDrawLayer
    >
      <SensorPositionFormPopup />
      <SensorFormNavigationArrows />
      <SensorPositionLayer />
    </FormMap>
  );
}

export const commonLocalizationStep = {
  label: 'localization',
  component: SensorPositionForm,
  validationFields: commonSensorPositionFormValidationFields,
};
